import { memo, useEffect, useRef, useState } from "react"
import { BaseBoxShapeUtil, HTMLContainer, TLShape, useEditor, useValue } from "tldraw"
import { useAuthStore } from "@/store/useAuthStore"
import { isTeacher } from "@/utils/role-utils"
import { renderMathElements, attachAnswerToggles, parseHtmlForRender } from "@ever-tutor/et-ui"

export const SLIDE_EMBED_TYPE = "slide-embed" as const

// Module-level cache: url → preprocessed slide content.
// Populated on first viewport render so that subsequent renders (including
// offscreen renders triggered by editor.toImage for AI screenshots) can apply
// content synchronously without a fetch — preventing empty slides in exports.
// Note: slides that have never entered the viewport will appear blank in AI screenshots —
// this is intentional (we only render what the user has seen).
const slideHtmlCache = new Map<string, { styles: string; slideHtml: string }>()

// Inject spinner keyframes once at module load — avoids creating a new <style> tag
// per loading slide, which would cause unnecessary style recalculations.
const SPINNER_STYLE_ID = "slide-embed-spinner-styles"
if (typeof document !== "undefined" && !document.getElementById(SPINNER_STYLE_ID)) {
  const style = document.createElement("style")
  style.id = SPINNER_STYLE_ID
  style.textContent = "@keyframes slide-embed-spin { to { transform: rotate(360deg); } }"
  document.head.appendChild(style)
}

// Module-level idle render queue — ensures off-screen slides fetch one at a time
// so they don't compete for bandwidth. Viewport renders bypass this queue entirely
// and cancel any pending idle entry for the same slide.
// Each queue item returns a Promise so drainIdleQueue waits for the fetch to
// complete before scheduling the next item (true serialization).
// Safari < 18 doesn't support requestIdleCallback — fall back to setTimeout(0).
// The callback receives a minimal IdleDeadline so callers don't need to branch.
const rIC: (cb: IdleRequestCallback, opts?: IdleRequestOptions) => number =
  typeof requestIdleCallback !== "undefined"
    ? requestIdleCallback
    : (cb) => setTimeout(() => cb({ didTimeout: false, timeRemaining: () => 0 }), 0) as unknown as number

const idleQueue: (() => Promise<void>)[] = []
let idleQueueRunning = false

function enqueueIdleRender(fn: () => Promise<void>): () => void {
  const wrapped = () => fn()
  idleQueue.push(wrapped)
  if (!idleQueueRunning) drainIdleQueue()
  return () => {
    const idx = idleQueue.indexOf(wrapped)
    if (idx !== -1) idleQueue.splice(idx, 1)
  }
}

function drainIdleQueue() {
  if (idleQueue.length === 0) { idleQueueRunning = false; return }
  idleQueueRunning = true
  rIC(() => {
    const next = idleQueue.shift()
    // Wait for the async fetch to finish before processing the next item.
    ;(next?.() ?? Promise.resolve()).then(drainIdleQueue)
  }, { timeout: 5000 })  // timeout forces execution if idle window never opens
}

// Extend TLDraw's type map so the editor accepts "slide-embed" as a known shape type.
declare module "tldraw" {
  interface TLGlobalShapePropsMap {
    [SLIDE_EMBED_TYPE]: { url: string; w: number; h: number }
  }
}

type TLSlideEmbedShape = TLShape<typeof SLIDE_EMBED_TYPE>

const SlideEmbedComponent = memo(function SlideEmbedComponent({ shape }: { shape: TLSlideEmbedShape }) {
  const editor = useEditor()
  const user = useAuthStore((s) => s.user)
  // Teachers see eye-toggle buttons to reveal/hide answer blocks.
  const showToggle = isTeacher(user?.role)

  const containerRef = useRef<HTMLDivElement>(null)
  // Guards against double-renders (viewport and idle queue can both trigger).
  const hasLoadedRef = useRef(false)
  // Cancels this slide's pending idle queue entry when it enters viewport.
  const cancelIdleRef = useRef<(() => void) | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)

  // Reactively tracks whether this shape is within the visible viewport.
  // useValue re-runs whenever the camera moves or the shape bounds change.
  const isInViewport = useValue(
    "in-viewport",
    () => {
      // Once loaded, return stable true — TLDraw stops subscribing to camera updates
      // for this shape, eliminating re-renders from viewport boundary crossings.
      if (isLoaded) return true
      const bounds = editor.getShapeMaskedPageBounds(shape.id)
      return bounds ? editor.getViewportPageBounds().collides(bounds) : false
    },
    [editor, shape.id, isLoaded],
  )

  useEffect(() => {
    if (hasLoadedRef.current || !shape.props.url) return

    // Writes preprocessed slide HTML into the container and renders KaTeX.
    const applyToContainer = (styles: string, slideHtml: string) => {
      if (hasLoadedRef.current) return  // guard against idle+viewport race
      const container = containerRef.current
      if (!container) return
      hasLoadedRef.current = true
      // innerHTML is safe here: slides are server-generated HTML fetched from our own S3/CDN,
      // never from user input. Scripts and viewport meta are stripped by parseHtmlForRender.
      // Same trust model as RevealRenderer.tsx (dangerouslySetInnerHTML) and SlideViewerPage.tsx.
      container.innerHTML = styles + slideHtml
      container.style.background = "white"
      // Run KaTeX synchronously — deferring it (e.g. requestIdleCallback) would show
      // raw LaTeX strings to the user before math renders, which is unacceptable.
      // Also required for editor.toImage() screenshots where async callbacks never fire.
      // Answer toggles need layout (getBoundingClientRect), so defer them to rAF.
      renderMathElements(container)
      requestAnimationFrame(() => attachAnswerToggles(container, container, showToggle))
      setIsLoaded(true)
    }

    // Fetches and parses slide HTML into cache without touching the DOM.
    // Storing only — no innerHTML — so images are not fetched for off-screen slides.
    const prefetch = async (): Promise<void> => {
      cancelIdleRef.current = null
      if (slideHtmlCache.has(shape.props.url)) return

      return fetch(shape.props.url)
        .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text() })
        .then((html) => {
          const parsed = parseHtmlForRender(html)
          if (!parsed) return
          slideHtmlCache.set(shape.props.url, parsed)
        })
        .catch((e) => console.warn("[SlideEmbedShapeUtil] prefetch failed:", e))
    }

    // Called when the slide enters the viewport.
    // Fast path: idle prefetch already populated the cache — just set innerHTML + KaTeX.
    // Slow path: user scrolled here before prefetch ran — fetch now and apply immediately.
    // Either way, images inside the slide only start loading here (not during prefetch),
    // so off-screen slides never trigger image downloads.
    const renderFromCache = () => {
      const cached = slideHtmlCache.get(shape.props.url)
      if (cached) { applyToContainer(cached.styles, cached.slideHtml); return }

      fetch(shape.props.url)
        .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text() })
        .then((html) => {
          const parsed = parseHtmlForRender(html)
          if (!parsed) return
          slideHtmlCache.set(shape.props.url, parsed)
          applyToContainer(parsed.styles, parsed.slideHtml)
        })
        .catch((e) => console.warn("[SlideEmbedShapeUtil] fetch failed:", e))
    }

    if (isInViewport) {
      // Cancel any pending idle prefetch and render immediately.
      cancelIdleRef.current?.()
      cancelIdleRef.current = null
      renderFromCache()
    } else if (cancelIdleRef.current === null) {
      // Enqueue a low-priority background prefetch — one at a time so HTML fetches
      // don't compete for bandwidth. Images are NOT fetched at this stage.
      cancelIdleRef.current = enqueueIdleRender(prefetch)
    }
  // shape.props.url intentionally omitted: URL never changes after shape creation.
  // showToggle intentionally omitted: user role never changes during component lifetime.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInViewport])

  return (
    <HTMLContainer>
      <div style={{ position: "relative", width: shape.props.w, height: shape.props.h }}>
        <div
          ref={containerRef}
          // Stop TLDraw from consuming pointer events on eye-toggle buttons.
          // Without this, pointerdown on a button drags the shape instead of clicking the button.
          onPointerDown={(e) => { if ((e.target as HTMLElement).closest("button")) e.stopPropagation() }}
          style={{ width: "100%", height: "100%", overflow: "hidden", background: "#f0f0f0" }}
        />
        {!isLoaded && isInViewport && (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "#f0f0f0", pointerEvents: "none",
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: "50%",
              border: "3px solid #d0d0d0", borderTopColor: "#888",
              animation: "slide-embed-spin 0.8s linear infinite",
            }} />
          </div>
        )}
      </div>
    </HTMLContainer>
  )
// Only re-render when id or props change — meta updates (slideContent) are irrelevant to rendering.
}, (prev, next) => prev.shape.id === next.shape.id && prev.shape.props === next.shape.props)

export class SlideEmbedShapeUtil extends BaseBoxShapeUtil<TLSlideEmbedShape> {
  static override type = SLIDE_EMBED_TYPE

  override getDefaultProps(): TLSlideEmbedShape["props"] {
    return { url: "", w: 960, h: 700 }
  }

  override component(shape: TLSlideEmbedShape) {
    return <SlideEmbedComponent shape={shape} />
  }

  override indicator(shape: TLSlideEmbedShape) {
    return <rect width={shape.props.w} height={shape.props.h} rx={4} ry={4} />
  }
}
